import React from 'react';
import type { MenuImage } from '../components/header/types';

export type NavigationStackEntry = {
    title: string;
    parentHref?: string;
    parentIcon?: MenuImage | null;
};

export interface UseNavigationStackReturn {
    navigationStack: NavigationStackEntry[];
    navigateForward: (
        _items: unknown,
        title: string,
        parentHref?: string,
        parentIcon?: MenuImage | null,
    ) => void;
    navigateBackward: () => void;
    resetNavigation: () => void;
}

/**
 * Tracks nested mobile nav focus for expand/collapse and leaf-link chrome (e.g. parent icon).
 * The first `items` argument to {@link navigateForward} is accepted for call-site parity; rendering uses the tree, not the stack payload.
 */
export function useNavigationStack<T>(_rootItems: T[], rootTitle: string): UseNavigationStackReturn {
    const [navigationStack, setNavigationStack] = React.useState<NavigationStackEntry[]>([
        { title: rootTitle },
    ]);

    const resetNavigation = React.useCallback(() => {
        setNavigationStack([{ title: rootTitle }]);
    }, [rootTitle]);

    const navigateBackward = React.useCallback(() => {
        setNavigationStack(prev => (prev.length <= 1 ? prev : prev.slice(0, -1)));
    }, []);

    const navigateForward = React.useCallback(
        (_items: unknown, title: string, parentHref?: string, parentIcon?: MenuImage | null) => {
            setNavigationStack(prev => [...prev, { title, parentHref, parentIcon }]);
        },
        [],
    );

    return { navigationStack, navigateForward, navigateBackward, resetNavigation };
}
